<script setup lang="ts">
import { computed, defineEmits, defineProps, RenderFunction, watch } from 'vue'
import { SelectOption } from '../../typings'
import BaseInputWrapper from './BaseInputWrapper.vue'
import { i18n } from '../../localization'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon, SelectorIcon } from '@heroicons/vue/solid'
import { twMerge } from 'tailwind-merge'
import { useFieldHelper } from './useBaseInput'

const props = defineProps<{
    modelValue: string
    label: string
    name: string
    icon?: RenderFunction
    required?: boolean
    required2?: boolean
    rules?: any
    options: SelectOption[]
    nullable?: boolean
    disabled?: boolean
    class?: string
    hint?: string
}>()

const { handleChange, errorMessage } = useFieldHelper(props.name, props.rules, { initialValue: props.modelValue })

const emit = defineEmits(['update:modelValue'])

watch(
    () => props.modelValue,
    (value) => {
        handleChange(value)
    }
)
function updateValue(value) {
    handleChange(value)
    emit('update:modelValue', value)
}

const value = computed({
    get: () => props.modelValue,
    set: (value) => {
        updateValue(value)
    },
})

const buttonText = computed(() => {
    if (value.value) {
        return props.options.find((option) => option.key === value.value).label
    } else {
        return i18n.global.t('forms.select_option')
    }
})
const iconClasses = computed(() => {
    return props.icon ? 'pl-10' : ''
})

const errorMessageClasses = computed(() => {
    return errorMessage.value
        ? 'border-red-300 pr-14 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : ''
})
</script>

<template>
    <BaseInputWrapper
        :name="props.name"
        :label="props.label"
        :icon="icon"
        :required="required"
        :required2="required2"
        :hint="props.hint"
        error-icon-class="pr-8">
        <Listbox v-model="value" :disabled="disabled" as="div">
            <div class="relative mt-1">
                <div v-if="icon" class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Component :is="icon" class="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>

                <ListboxButton
                    :class="
                        twMerge(
                            'relative w-full cursor-default rounded-md border border-gray-300 py-3 pl-3 pr-10 text-left shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm',
                            iconClasses,
                            errorMessageClasses,
                            props.class
                        )
                    ">
                    <span class="block truncate">{{ buttonText }}</span>
                    <span
                        v-if="!disabled"
                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <SelectorIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                </ListboxButton>

                <transition
                    leave-active-class="transition ease-in duration-100"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0">
                    <ListboxOptions
                        class="absolute z-10 mt-1 max-h-60 w-full list-none overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        <ListboxOption
                            v-for="option in options"
                            :key="option.key"
                            v-slot="{ active, selected }"
                            as="template"
                            :disabled="option.disabled"
                            :value="option.key">
                            <li
                                :class="[
                                    active ? 'bg-primary text-white' : 'text-gray-900',
                                    'relative cursor-default select-none py-2 pl-3 pr-9',
                                ]">
                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                    {{ option.label }}
                                </span>

                                <span
                                    v-if="selected"
                                    :class="[
                                        active ? 'text-white' : 'text-primary',
                                        'absolute inset-y-0 right-0 flex items-center pr-4',
                                    ]">
                                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                                </span>
                            </li>
                        </ListboxOption>
                    </ListboxOptions>
                </transition>
            </div>
        </Listbox>
    </BaseInputWrapper>
</template>
